import {Button, CardHeader, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField} from "@mui/material";
import axios from "axios";
import * as React from "react";
import {DateTimePicker, LocalizationProvider} from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {Config} from "../../config/config";
import {Helpers} from "../../utils/helpers";
import StarIcon from '@mui/icons-material/Star';
import {PopupMessage} from "../../utils/message";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export class BattleReport extends React.Component<any, any> {
    constructor(props: any) {
        super(props);

        this.state = {
            startTime: new Date(),
            endTime: new Date(),
            battleStats: [],
            totalAmount: 0,
            loading: false,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    async fetchData() {
        const url = `${Config.getInstance.apiUrl}/api/report/battle_report`;

        this.setState({
            loading: true,
        });

        const headers = {
            Authorization: Helpers.getSessionId(),
        }

        const response = await axios.get(url, {
            params: {
                startTime: this.state.startTime,
                endTime: this.state.endTime,
            },
            headers
        });

        const data = response.data;
        const success = data.success;

        this.setState({
            loading: false,
        });

        if (success) {
            const battleStats = data.battleStats;
            const totalAmount = data.totalAmount;

            this.setState({
                battleStats,
                totalAmount,
            });
        } else {
            PopupMessage.error("Failed to fetch data")
        }

    }

    handleChangeTime = (key: string, value: any) => {
        this.setState({
            [key]: value,
        });
    }

    handleChangeState = (key: string, event: any) => {
        const value = event.target.value;

        this.setState({
            [key]: value,
        });
    }

    formatNumber = (number) => {
        // format number from 1000 to 1,000
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);

        PopupMessage.success("Copied to clipboard");
    }

    onCopyContent = () => {
        const {battleStats} = this.state;

        // Battle Stats: {hqLevel, total, groupByStars: [{stars, amount}]}

        let string = ``;

        const totalAmount = this.state.totalAmount;

        /**
         * String format:
         * Total Amount: 100,000
         * 
         * <b>HQ Level 1</b> - Total: 100,000 - %
         * 0 Stars: 10,000 - %
         * 1 Stars: 20,000 - %
         * 2 Stars: 30,000 - %
         * 3 Stars: 40,000 - %
         * 
         * HQ Level 2 - Total: 200,000 - %
         * ...
         */

        string += `Total Battles: ${this.formatNumber(totalAmount)}\n\n`;

        if (totalAmount > 0) {
            battleStats.forEach((battleStat: any) => {
                const {hqLevel, total, groupByStars} = battleStat;

                string += `HQ Level ${hqLevel} - Total: ${this.formatNumber(total)} - ${(total/totalAmount * 100).toFixed(3)}% \n `;

                groupByStars.forEach((star: any) => {
                    string += `- ${star.stars} Stars: ${this.formatNumber(star.amount)} - ${(star.amount/total * 100).toFixed(3)}%\n`;
                });

                string += '\n';
            });
        }

        this.copyToClipboard(string);
    }

    renderFilter() {
        return <Paper>
            <Grid container sm={12} rowSpacing={2}>
                <Grid item container sm={12}>
                    <Grid item container sm={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="Start Time"
                                value={this.state.startTime}
                                onChange={(e) => this.handleChangeTime("startTime", e)}
                                renderInput={(params) => <TextField {...params} size="small" />}
                                ampm={false}
                            />
                        </LocalizationProvider>
                    </Grid>

                </Grid>

                <Grid item container sm={12}>
                    <Grid item sm={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="End Time"
                                value={this.state.endTime}
                                onChange={(e) => this.handleChangeTime("endTime", e)}
                                renderInput={(params) => <TextField {...params} size="small" />}
                                ampm={false}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>

                <Grid item container sm={12} justifyContent={"flex-end"}>
                    <Grid item sm={1}>
                        <Button
                            color="primary"
                            onClick={this.fetchData.bind(this)}
                            variant="contained"
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    }

    renderContent() {

        const {battleStats} = this.state;
        return <Table>
            <TableHead>
                <TableRow>
                    <TableCell>HQ Level</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell><><StarIcon style={{color: 'gold'}} />x0</></TableCell>
                    <TableCell><><StarIcon style={{color: 'gold'}} />x1</></TableCell>
                    <TableCell><><StarIcon style={{color: 'gold'}} />x2</></TableCell>
                    <TableCell><><StarIcon style={{color: 'gold'}} />x3</></TableCell>
                    <TableCell><ContentCopyIcon onClick={this.onCopyContent} /></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {
                    this.state.loading ?
                        <TableRow key="loading-row">
                            <TableCell colSpan={11}>
                                {Helpers.renderSkeletonRows(5, 50)}
                            </TableCell>
                        </TableRow>

                        :

                        battleStats.map((battleStat: any, index: number) => {
                            const {hqLevel, total, groupByStars} = battleStat;

                            const amountByStars = [0, 0, 0, 0];
                            groupByStars.forEach((star: any) => {
                                amountByStars[star.stars] = star.amount;
                            });

                            return <TableRow key={index}>
                                <TableCell>{hqLevel}</TableCell>
                                <TableCell>{total}</TableCell>
                                {
                                    amountByStars.map((amount: any, index: number) => {
                                        return <TableCell key={index}>{amount}</TableCell>
                                    })
                                }
                            </TableRow>
                        })
                }
            </TableBody>
        </Table>
    }

    render() {
        return <Paper className="contentPadding">
            <CardHeader title="Battle Report" />
            {this.renderFilter()}
            {this.renderContent()}
        </Paper>
    }
}